import { ArticleType, PublicAmorgUser, SimpleArticleList, SubscriberArticleSort, SurveySegment } from "../amorg-common";
import { surveyValidation } from "../amorg-common/modules/fieldValidation/fields/surveyFieldValidation";

export type SegmentationViewLocation = 'survey-report';
export type SegmentationViewMode = 'pills' | 'table';

export type SegmentationFilterLocation = 'survey-report';

export type ClientArticlePageFilter = {
    topicId:string, 
    typeId:string,
    ownerUserId:string,
    sort:{ sortBy: SubscriberArticleSort, ascending: boolean }, 
    page:number
}
export function getDefaultClientArticlePageFilter(): ClientArticlePageFilter 
{
    return { topicId: '', typeId: '', ownerUserId:'', sort: { sortBy: 'actionStatus', ascending: true }, page: 1 };
}


export const amorgLocalStorage = {
    surveySegmentation:{
        viewMode:   
        {
            getKey: (location:SegmentationViewLocation, surveyId:string) => `surveySegmentation_viewMode_${location}_${surveyId}`,
            readOrCreate: (key:string) => readValue<SegmentationViewMode>(key, 'pills'),
            write:(key:string, value:SegmentationViewMode) => writeValue(key, value)
        },
        filter:
        {
            getKey: (location: SegmentationFilterLocation, surveyId: string) => `surveySegmentation_filter_${location}_${surveyId}`,
            read: (key:string) => readValue<SurveySegment[][]>(key, []),
            write: (key:string, value:SurveySegment[][]) => writeValue(key, value)
        },
        // This should replace the above filter once we have implemented the new segmentation filter UI and logic, but for now we need both to avoid losing users' existing filters
        filter2:{
            readFromStorage:(surveyId:string) => {
                const val = readValue<SurveySegment[][]>(`surveySegmentation_filter_survey-report_${surveyId}`, []);
                if(!surveyValidation.surveySegment.validateUniqueListOfLists(val))
                {
                    return [];
                }
                return val;
            },
            writeToStorage:(surveyId:string, value:SurveySegment[][]) => writeValue(`surveySegmentation_filter_survey-report_${surveyId}`, value),
        }
    },
    surveyReport: {
        viewMode:{
            getKey:() => `surveyReport_viewMode`,
            readOrCreate: (key:string) => {
                const value = readValue<string>(key, 'overview')
                if(value === 'overview' || value === 'comparison' || value === 'details')
                {
                    return value;
                }
                return 'overview';
            },
            write: (key:string, value:'overview' | 'comparison' | 'details') => writeValue(key, value)
        }
    },
    mainMenu:{
        getKey: (menuKey:string) => `menu-collapsed-${menuKey || 'default'}`,
        read: (key:string) => readValue<boolean>(key, false),
        write: (key:string, value:boolean) => writeValue(key, value)
    },
    articles:{
        readFilter: (
            listId:string, 
            lists:SimpleArticleList[], 
            types:ArticleType[], 
            owners:PublicAmorgUser[]) => articleListFilterReader(listId).safeRead(lists, types, owners),

        saveFilter: (listId:string, filter:ClientArticlePageFilter) => articleListFilterReader(listId).save(filter),
        articlesLight: {
            listId: {
                getKey: 'ArticleLightPageFilter_listId',
                read: (key:string) => readValue<string>(key, ''),
                write: (key:string, value:string) => writeValue(key, value)
            }
        }
    }
}

function readValue<T>(key:string, defaultValue:T):T
{
    const item = window.localStorage.getItem(key);
    if(item === null)
    {
        writeValue<T>(key, defaultValue);
        return defaultValue;
    }

    try {
        return JSON.parse(item) as T;
    }
    catch {
        writeValue<T>(key, defaultValue);
        return defaultValue;
    }
}

function writeValue<T>(key:string, value:T):void
{
    window.localStorage.setItem(key, JSON.stringify(value));
}


function articleListFilterReader(listId:string)
{
    return {
        safeRead:(lists:SimpleArticleList[], types:ArticleType[], owners:PublicAmorgUser[]) => 
            readSafeLocalStorageFilter(listId, lists, types, owners),
        
        save:(filter:ClientArticlePageFilter) => saveFilter(filter)
    }

    function readSafeLocalStorageFilter(
        listId:string,
        lists:SimpleArticleList[], 
        types:ArticleType[], 
        owners:PublicAmorgUser[]
    ): ClientArticlePageFilter
    {
        let filter = readLocalStorageFilter(listId);
        console.log('Read filter from localStorage');
        console.log('listId:', listId)
        console.log('Filter before validation:', filter);

        if(listId !== '' && !lists.some(l => l.id === listId))
        {
            console.log('ListId from localStorage is no longer valid, resetting filter to default');
            filter = { ...getDefaultClientArticlePageFilter() };
            console.log('New filter after resetting invalid listId', filter);
            return filter;
        }


        // If the topicId, typeId or ownerUserId from localStorage is not valid anymore, reset those specific fields to empty string (which is the "no filter" value), but keep the rest of the filter (like sort and page) as they are not related to the list and can still be valid
        if(filter.topicId!=='' && !lists.some(l => l.topics.some(t => t.id === filter.topicId)))
        {
            console.log('TopicId from localStorage is no longer valid, resetting topicId filter');
            filter.topicId = '';
        }
        if(filter.typeId!=='' && !types.map(t => t.id).includes(filter.typeId))
        {
            console.log('TypeId from localStorage is no longer valid, resetting typeId filter');
            filter.typeId = '';
        }
        if(filter.ownerUserId !== '' && !owners.map(o => o.id).includes(filter.ownerUserId))
        {
            console.log('OwnerUserId from localStorage is no longer valid, resetting ownerUserId filter');
            filter.ownerUserId = '';
        }
        
        saveFilter(filter);

        return filter;
    }

    function readLocalStorageFilter(listId?:string): ClientArticlePageFilter
    {
        const filter:ClientArticlePageFilter = {
            ...getDefaultClientArticlePageFilter()
        };

        const filterString = localStorage.getItem('ArticleListPageFilter_' + (listId || ''));
        try {
            const parsedFilter = JSON.parse(filterString || '{}');

            filter.topicId = parsedFilter.topicId || '';
            filter.typeId = parsedFilter.typeId || '';
            filter.ownerUserId = parsedFilter.ownerUserId || '';
            filter.sort = parsedFilter.sort || { sortBy: 'actionStatus', ascending: true };
            filter.page = parsedFilter.page || 1;
        }
        catch(e) {
            // Saved filter is invalid - save new version
            saveFilter(filter);
        }

        return filter;
    }

    function saveFilter(filter:ClientArticlePageFilter)
    {
        localStorage.setItem('ArticleListPageFilter_' + listId, JSON.stringify(filter));
    }
}